const LOCAL_API_HOST = 'api.localhost.local:3000';
const LOCAL_SITE_API_HOST_SUFFIX = '.api.localhost.local:3000';
const LOCAL_APP_HOST = 'localhost:3100';
const DEFAULT_SITE_API_PREFIX = '/site-api';

const envValue = (key) => {
  const value = process.env[key];
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
};

const normalizeScheme = (value, fallback = 'http') => {
  const scheme = (value || fallback).replace(/:\/?\/?$/, '').toLowerCase();
  return scheme === 'https' ? 'https://' : 'http://';
};

const normalizeHost = (value) => (
  value
    .replace(/^https?:\/\//, '')
    .replace(/^\/\//, '')
    .replace(/\/+$/, '')
);

const normalizeHostSuffix = (value) => {
  const host = normalizeHost(value);
  return host.startsWith('.') ? host : `.${host}`;
};

const normalizePath = (value, fallback = '') => {
  const path = value || fallback;
  if (!path) return '';
  return `/${path.replace(/^\/+/, '').replace(/\/+$/, '')}`;
};

const splitLegacyHostPath = (value, fallbackHost, fallbackPath) => {
  const normalized = normalizeHost(value || `${fallbackHost}${fallbackPath}`);
  const pathStart = normalized.indexOf('/');

  if (pathStart === -1) {
    return {
      host: normalized || fallbackHost,
      path: normalizePath(undefined, fallbackPath),
    };
  }

  return {
    host: normalized.slice(0, pathStart) || fallbackHost,
    path: normalizePath(normalized.slice(pathStart), fallbackPath),
  };
};

const joinUrl = (baseUrl, path = '') => `${baseUrl.replace(/\/+$/, '')}${normalizePath(path)}`;

const browserLocation = () => (typeof window === 'undefined' ? undefined : window.location);

const legacyBase = splitLegacyHostPath(
  envValue('REACT_APP_API_BASE_URL'),
  LOCAL_API_HOST,
  '/api/v1'
);
const legacySite = splitLegacyHostPath(
  envValue('REACT_APP_API_USER_URL'),
  LOCAL_SITE_API_HOST_SUFFIX,
  '/api/v1/sites'
);

const apiScheme = normalizeScheme(envValue('REACT_APP_API_SCHEME') || envValue('REACT_APP_API_HTTP'));
const accessMode = envValue('REACT_APP_PUBLIC_ACCESS_MODE') === 'ip' ? 'ip' : 'domain';
const apiBaseHost = normalizeHost(envValue('REACT_APP_API_BASE_HOST') || legacyBase.host);
const apiBasePath = normalizePath(envValue('REACT_APP_API_BASE_PATH'), legacyBase.path);
const apiSiteHostSuffix = normalizeHostSuffix(envValue('REACT_APP_API_SITE_HOST_SUFFIX') || legacySite.host);
const apiSitePath = normalizePath(envValue('REACT_APP_API_SITE_PATH'), legacySite.path);
const apiSitePrefix = normalizePath(envValue('REACT_APP_API_SITE_PREFIX'), DEFAULT_SITE_API_PREFIX);
const apiSensorHost = normalizeHost(envValue('REACT_APP_API_SENSOR_HOST') || envValue('REACT_APP_API_SENSOR_URL') || apiBaseHost);
const appScheme = normalizeScheme(
  envValue('REACT_APP_APP_SCHEME'),
  apiScheme.startsWith('https') ? 'https' : 'http'
);
const appHost = normalizeHost(envValue('REACT_APP_APP_HOST') || LOCAL_APP_HOST);

export const publicConfig = {
  accessMode,
  apiScheme,
  apiBaseHost,
  apiBasePath,
  apiSiteHostSuffix,
  apiSitePath,
  apiSitePrefix,
  apiSensorHost,
  appScheme,
  appHost,
};

const browserOrigin = () => browserLocation()?.origin || `${publicConfig.apiScheme}${publicConfig.apiBaseHost}`;
const browserHostname = () => browserLocation()?.hostname || publicConfig.apiBaseHost.split(':')[0];
const browserScheme = () => normalizeScheme(
  browserLocation()?.protocol,
  publicConfig.apiScheme.startsWith('https') ? 'https' : 'http'
);

const apiOrigin = () => (
  publicConfig.accessMode === 'ip'
    ? browserOrigin()
    : `${publicConfig.apiScheme}${publicConfig.apiBaseHost}`
);

export const apiBaseUrl = (path = '') => joinUrl(
  `${apiOrigin()}${publicConfig.apiBasePath}`,
  path
);

export const siteApiBaseUrl = (siteSubdomain, path = '') => {
  const baseUrl = publicConfig.accessMode === 'ip'
    ? `${browserOrigin()}${publicConfig.apiSitePrefix}/${encodeURIComponent(siteSubdomain)}${publicConfig.apiSitePath}`
    : `${publicConfig.apiScheme}${siteSubdomain || publicConfig.apiSiteHostSuffix.slice(1)}${siteSubdomain ? publicConfig.apiSiteHostSuffix : ''}${publicConfig.apiSitePath}`;

  return joinUrl(baseUrl, path);
};

export const apiSensorUrl = (path = '') => joinUrl(
  publicConfig.accessMode === 'ip'
    ? `${browserScheme()}${browserHostname()}:88`
    : `${publicConfig.apiScheme}${publicConfig.apiSensorHost}`,
  path
);

export const uploadUrl = (uploadId) => apiBaseUrl(`/uploads/${uploadId}`);
